import type { ThemeOverrides } from '@/lib/theme';
import { justBrand } from './just';
import { valepagBrand } from './valepag';
import type { BrandConfig } from './types';

export type { BrandConfig, LogoProps } from './types';

const BRANDS: Record<string, BrandConfig> = {
  just: justBrand,
  valepag: valepagBrand,
};

let cached: BrandConfig | null = null;

/**
 * Resolve o brand ativo a partir de VITE_BRAND (build-time). Default 'just'
 * se a env var nao existir ou nao bater com nenhum brand conhecido - build
 * sem VITE_BRAND continua identico ao comportamento de hoje.
 */
export function resolveBrand(): BrandConfig {
  if (cached) return cached;
  const id = (import.meta.env.VITE_BRAND as string | undefined)?.trim().toLowerCase();
  cached = (id && BRANDS[id]) || justBrand;
  return cached;
}

/**
 * Expande a paleta do brand em overrides de tokens CSS
 * (--brand-navy-{step}, --brand-goiaba-{step}), no formato que
 * `applyConfig()` (lib/theme.ts) ja sabe aplicar em :root (incluindo o
 * -rgb triple derivado, necessario pros usos com alpha channel).
 */
export function paletteToOverrides(brand: BrandConfig): ThemeOverrides {
  const overrides: ThemeOverrides = {};
  for (const [step, hex] of Object.entries(brand.palette.navy)) {
    overrides[`--brand-navy-${step}`] = hex;
  }
  for (const [step, hex] of Object.entries(brand.palette.accent)) {
    overrides[`--brand-goiaba-${step}`] = hex;
    // brand-purple-* e alias legado que aponta pra NAVY (nao pro accent) -
    // ver tokens.css secao "LEGACY". Nao sobrescrevemos aqui de proposito:
    // o brand-purple-* fica com o valor navy do proprio brand (aplicado
    // abaixo), preservando a semantica documentada.
  }
  for (const [step, hex] of Object.entries(brand.palette.navy)) {
    overrides[`--brand-purple-${step}`] = hex;
  }
  // purple-950/1000 nao tem degrau navy correspondente (escala navy para em
  // 900) - tokens.css aponta os dois pro navy-900 (degrau mais escuro).
  overrides['--brand-purple-950'] = brand.palette.navy['900'];
  overrides['--brand-purple-1000'] = brand.palette.navy['900'];
  return overrides;
}

const FAVICON_MIME_BY_EXT: Record<string, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  ico: 'image/x-icon',
};

/**
 * Aplica titulo, favicon e theme-color do brand no <head>. Chamado uma vez
 * no boot (main.tsx), antes da arvore React montar.
 */
export function applyBrandDocumentMeta(brand: BrandConfig): void {
  if (typeof document === 'undefined') return;
  document.title = brand.displayName;

  const iconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (iconLink) {
    iconLink.href = brand.faviconHref;
    // Fix: o <link> em index.html vem com type="image/png" fixo (favicon
    // default da JUST). Trocar so o href sem trocar o type deixa o
    // navegador com um MIME type mentiroso (ex: favicon.svg anunciado como
    // image/png) - Chrome/Firefox ignoram silenciosamente e mantem o
    // favicon antigo/cache. Deriva o type certo pela extensao do arquivo.
    const ext = brand.faviconHref.split('.').pop()?.toLowerCase() ?? '';
    iconLink.type = FAVICON_MIME_BY_EXT[ext] ?? '';
  }

  const themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColorMeta) themeColorMeta.content = brand.themeColorMeta;
}
